import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';
import 'moment/locale/es';

moment.locale('es');

const API_URL = process.env.REACT_APP_API_URL || 'https://botcombustible.netizar.com';

const AutTicketModal = ({ open, gasto, onClose }) => {
  const [numeroTicket, setNumeroTicket] = useState('');
  const [monto, setMonto] = useState('');
  const [litros, setLitros] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    if (gasto) {
      setNumeroTicket(gasto.numero_ticket || '');
      setMonto(gasto.monto_extraido || '');
      setLitros(gasto.litros_extraidos || '');
      setObservaciones(gasto.observaciones || '');
    }
  }, [gasto]);

  const handleCambiarEstado = async (nuevoEstado) => {
    setLoading(true);
    try {
      // 1. Primero guardar los datos (numero_ticket, monto, litros)
      const guardarResponse = await fetch(`${API_URL}/api/gastos/${gasto.id}/datos`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numero_ticket: numeroTicket || null,
          monto_extraido: monto ? parseFloat(monto) : null,
          litros_extraidos: litros ? parseFloat(litros) : null
        })
      });

      if (!guardarResponse.ok) {
        setMensaje({ tipo: 'error', texto: 'Error al guardar datos' });
        return;
      }

      // 2. Luego cambiar el estado
      const estadoResponse = await fetch(`${API_URL}/api/gastos/${gasto.id}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estado: nuevoEstado,
          observaciones: observaciones || null
        })
      });

      if (estadoResponse.ok) {
        setMensaje({
          tipo: 'success',
          texto: `Gasto ${nuevoEstado === 'aprobado' ? 'aprobado' : 'rechazado'} correctamente`
        });
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setMensaje({ tipo: 'error', texto: 'Error al cambiar estado' });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  const handleDescargar = () => {
    window.open(`${API_URL}/api/gastos/${gasto.id}/ticket`, '_blank');
  };

  const handleAbrirImagenNuevaVentana = () => {
    window.open(imagenUrl, '_blank');
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente': return 'warning';
      case 'aprobado': return 'success';
      case 'rechazado': return 'error';
      default: return 'default';
    }
  };

  // Validar si se pueden habilitar los botones de aprobar/rechazar
  // Solo verificar que haya litros (el monto puede faltar)
  const puedeAprobarRechazar = () => {
    return litros && parseFloat(litros) > 0;
  };

  if (!gasto) return null;

  const imagenUrl = `${API_URL}/uploads/${gasto.foto_ticket_path}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Ticket de Combustible - {gasto.patente_vehiculo}
          </Typography>
          <Box>
            <Chip
              label={gasto.estado.toUpperCase()}
              color={getEstadoColor(gasto.estado)}
              sx={{ mr: 1 }}
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {mensaje && (
          <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>
            {mensaje.texto}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Columna izquierda - Imagen */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                overflow: 'auto',
                height: 500,
                cursor: 'pointer'
              }}
              onClick={handleAbrirImagenNuevaVentana}
              title="Click para ampliar en nueva ventana"
            >
              <img
                src={imagenUrl}
                alt="Ticket"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDescargar}
              sx={{ mt: 1 }}
            >
              Descargar Imagen
            </Button>
          </Grid>

          {/* Columna derecha - Datos */}
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom>
              Información del Registro
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="DNI"
                  value={gasto.dni_empleado}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Empleado"
                  value={gasto.nombre_empleado || '-'}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Patente"
                  value={gasto.patente_vehiculo}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Kilometraje"
                  value={`${gasto.kilometraje.toLocaleString()} km`}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Fecha de Carga"
                  value={moment(gasto.fecha_carga).format('DD/MM/YYYY HH:mm')}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Datos del Ticket
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Número de Ticket"
                  value={numeroTicket}
                  onChange={(e) => setNumeroTicket(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={gasto.estado !== 'pendiente'}
                  placeholder="123456789"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Monto ($)"
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={gasto.estado !== 'pendiente'}
                  helperText="Opcional"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Litros *"
                  type="number"
                  value={litros}
                  onChange={(e) => setLitros(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={gasto.estado !== 'pendiente'}
                  helperText="Obligatorio"
                  error={gasto.estado === 'pendiente' && (!litros || parseFloat(litros) <= 0)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  disabled={gasto.estado !== 'pendiente'}
                />
              </Grid>

              {gasto.estado === 'pendiente' && !puedeAprobarRechazar() && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Completá los litros para poder aprobar o rechazar
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {gasto.estado === 'pendiente' && (
          <>
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleCambiarEstado('rechazado')}
              disabled={loading || !puedeAprobarRechazar()}
            >
              Rechazar
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleCambiarEstado('aprobado')}
              disabled={loading || !puedeAprobarRechazar()}
            >
              Aprobar
            </Button>
          </>
        )}
        <Button onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutTicketModal;